import { ArrowLeftOutlined } from '@ant-design/icons'
import { List } from '@refinedev/antd'
import {
  IResourceComponentsProps
} from '@refinedev/core'
import { Space, Tabs } from 'antd'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from "react-router-dom"

import { useGetDetailCompany } from '../../../api/company'
import { useDeleteCompany } from '../../../utils/hooks/useDeleteCompany.hook'
import { useNotificationOkCancel } from '../../../utils/hooks/useNotificationOkCancel.hook'
import { confirmModalDeleteProps, confirmModalUnsavedChangesProps } from '../../components/constants'
import '../../styles/index.css'
// import CompanyBankAccounts from './components/company-banks'
import { CompanyInformation } from './components/company-information'
import { CompanyWallets } from './components/company-wallets'

export const CompanyEdit: FC<IResourceComponentsProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const companyId = id ? parseInt(id, 10) : undefined;
  const queryParams = new URLSearchParams(location.search);
  const tabFromUrl = queryParams.get('tab');
  const [deletingId, setDeletingId] = useState<number>()
  const [activeTab, setActiveTab] = useState<"info" | "bank" | "wallet">(
    (tabFromUrl as "info" | "bank" | "wallet") || 'info'
  )
  const [pendingTab, setPendingTab] = useState<"info" | "bank" | "wallet" | null>(null)
  const checkUnsavedChangesRef = useRef<(() => Promise<boolean>) | null>(null)
  const resetFormRef = useRef<(() => void) | null>(null)

  const { deleteCompany } = useDeleteCompany()

  const { data: loadData, isLoading: loadIsLoading } = useGetDetailCompany(
    id ? Number(id) : null,
    !!id
  );


  const onOkDelete = useCallback(async () => {
    if (deletingId !== undefined) {
      setDeletingId(undefined)
      await deleteCompany(deletingId)
      navigate('/company')
    }
  }, [deletingId, deleteCompany, navigate])

  const onCancelDelete = useCallback(() => {
    setDeletingId(undefined)
  }, [])

  const { openNotification, contextHolder } = useNotificationOkCancel({
    ...confirmModalDeleteProps,
    onOk: onOkDelete,
    onCancel: onCancelDelete,
  })

  const onUnsavedChangesOk = useCallback(() => {
    if (resetFormRef.current) {
      resetFormRef.current()
    }
    if (pendingTab) {
      setActiveTab(pendingTab)
      navigate(`/company/${id}?tab=${pendingTab}`, { replace: true })
      setPendingTab(null)
    }
  }, [pendingTab, navigate, id])

  const onUnsavedChangesCancel = useCallback(() => {
    setPendingTab(null)
  }, [])

  const {
    openNotification: openUnsavedChangesNotification,
    contextHolder: unsavedChangesContextHolder,
  } = useNotificationOkCancel({
    ...confirmModalUnsavedChangesProps,
    onOk: onUnsavedChangesOk,
    onCancel: onUnsavedChangesCancel,
  })

  useEffect(() => {
    if (deletingId !== undefined) {
      openNotification()
    }
  }, [deletingId, openNotification])

  useEffect(() => {
    if (pendingTab !== null) {
      openUnsavedChangesNotification()
    }
  }, [pendingTab, openUnsavedChangesNotification])

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tab = queryParams.get('tab');
    if (tab && (tab === 'info' || tab === 'wallet') && tab !== activeTab) {
      setActiveTab(tab);
    }
  }, [location.search, activeTab])

  const refetchList = useCallback(() => {
    // This will be handled by individual components
  }, [])

  const handleTabChange = useCallback(
    (newTab: string) => {
      if (activeTab === 'info' && checkUnsavedChangesRef.current) {
        checkUnsavedChangesRef.current().then((hasUnsavedChanges) => {
          if (hasUnsavedChanges) {
            setPendingTab(newTab as "info" | "bank" | "wallet")
          } else {
            setActiveTab(newTab as "info" | "bank" | "wallet")
            navigate(`/company/${id}?tab=${newTab}`, { replace: true })
          }
        })
      } else {
        setActiveTab(newTab as "info" | "bank" | "wallet")
        navigate(`/company/${id}?tab=${newTab}`, { replace: true })
      }
    },
    [activeTab, navigate, id]
  )

  const handleUnsavedChangesCheck = useCallback(
    (checkFn: () => Promise<boolean>, resetFn: () => void) => {
      checkUnsavedChangesRef.current = checkFn
      resetFormRef.current = resetFn
    },
    []
  )

  const handleBack = useCallback(() => {
    navigate('/company')
  }, [navigate])

  const tabItems = [
    {
      key: 'info',
      label: 'Information',
      children: (
        <CompanyInformation
          editingId={companyId}
          refetchList={refetchList}
          onUnsavedChangesCheck={handleUnsavedChangesCheck}
          isActive={activeTab === 'info'}
          userId={loadData?.data?.userId}
          loadData={loadData}
          isLoading={loadIsLoading}
        />
      ),
    },
    {
      key: 'wallet',
      label: 'Wallets',
      children: (
        <CompanyWallets
          companyId={companyId}
          isActive={activeTab === 'wallet'}
          userId={loadData?.data?.userId}
        />
      ),
    },
    // {
    //   key: 'bank',
    //   label: 'Banks',
    //   children: (
    //     <CompanyBankAccounts
    //       tabActive={activeTab}
    //       id={companyId?.toString() || ''}
    //       isActive={activeTab === 'bank'}
    //       userId={loadData?.data?.userId}
    //     />
    //   ),
    // },
  ]

  if (!companyId) {
    return null;
  }

  return (
    <>
      <List
        title={
          <Space>
            <ArrowLeftOutlined
              onClick={handleBack}
              className="back-icon"
            />
            <span>{loadData?.data?.name ?? 'Provider Details'}</span>
          </Space>
        }
      >
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={tabItems}
        />
      </List>
      {contextHolder}
      {unsavedChangesContextHolder}
    </>
  )
}

